import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Car, ListChecks, Clock, Truck, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { transportApi } from '@/services/transportApi';
import { KpiTile, DateRangeFilter, inDateRange } from '@/components/transport/DashboardBits';
import { ClubbedInto, ClubbedFrom } from '@/components/transport/ClubbedInfo';

const STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700', ASSIGNED: 'bg-blue-100 text-blue-700',
  IN_TRANSIT: 'bg-indigo-100 text-indigo-700', COMPLETED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700', CLUBBED: 'bg-violet-100 text-violet-700',
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return '—'; }
};
const route = (r) => r.route_summary || `${r.pickup || '—'} → ${r.drop || '—'}`;

const RequestBoard = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [assignVeh, setAssignVeh] = useState('');
  const [assignDrv, setAssignDrv] = useState('');
  const [note, setNote] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignType, setAssignType] = useState('');
  const [hireOpen, setHireOpen] = useState(false);
  const [hire, setHire] = useState({ registration_no: '', vehicle_type: '' });
  const [types, setTypes] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const filtered = useMemo(() => requests.filter(r => inDateRange(r.created_at, from, to)), [requests, from, to]);
  const kpis = useMemo(() => ({
    total: filtered.length,
    pending: filtered.filter(r => r.status === 'PENDING').length,
    inTransit: filtered.filter(r => r.status === 'IN_TRANSIT').length,
    completed: filtered.filter(r => r.status === 'COMPLETED').length,
    rejected: filtered.filter(r => r.status === 'REJECTED').length,
  }), [filtered]);

  const load = useCallback(async () => {
    const [r, v, d] = await Promise.all([transportApi.listRequests(), transportApi.listVehicles({ active: true }), transportApi.listDrivers({ active: true })]);
    setRequests(r); setVehicles(v); setDrivers(d);
  }, []);
  useEffect(() => {
    load().catch(() => toast.error('Failed to load requests'));
    transportApi.getSettings().then(s => setTypes(s.vehicle_types || [])).catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 20000);
    return () => clearInterval(t);
  }, [load]);

  const openAssign = (r) => {
    setAssignFor(r);
    setAssignType(r.vehicle_type);
    const matchVeh = vehicles.filter(v => v.vehicle_type === r.vehicle_type);
    setAssignVeh(r.vehicle_id || matchVeh[0]?.id || '');
    setAssignDrv(r.driver_id || '');
    setNote('');
  };
  const eligibleDrivers = () => {
    const v = vehicles.find(x => x.id === assignVeh);
    const ids = v?.eligible_driver_ids || [];
    return drivers.filter(d => ids.length === 0 || ids.includes(d.id));
  };
  const vehOnOtherReq = (vehId) => requests.find(r => r.id !== assignFor?.id && r.vehicle_id === vehId && r.status === 'ASSIGNED');
  const doAssign = async () => {
    if (!assignVeh || !assignDrv) { toast.error('Pick a vehicle and driver'); return; }
    if (assigning) return;
    setAssigning(true);
    try { await transportApi.assignRequest(assignFor.id, { vehicle_id: assignVeh, driver_id: assignDrv, note, vehicle_type: assignType }); toast.success('Assigned'); setAssignFor(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setAssigning(false); }
  };
  const doReject = async (r) => { const reason = window.prompt('Reason for rejection?') || ''; await transportApi.rejectRequest(r.id, reason); load(); };
  const addHired = async () => {
    if (!hire.registration_no.trim() || !hire.vehicle_type) { toast.error('Registration & type required'); return; }
    try { await transportApi.createVehicle({ ...hire, ownership: 'hired', active: true, eligible_driver_ids: drivers.map(d => d.id) }); toast.success('Hired vehicle added — now assignable'); setHireOpen(false); setHire({ registration_no: '', vehicle_type: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="transport-request-board-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/transport')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-xl font-bold font-display">Vehicle Request Board</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="bg-white/10 text-white border-white/20" onClick={() => setHireOpen(true)} data-testid="hire-vehicle-btn"><Car className="w-4 h-4 mr-1" />Hire Vehicle</Button>
            <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => load()} data-testid="requests-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4" data-testid="requests-kpis">
          <KpiTile label="Total Requests" value={kpis.total} icon={ListChecks} accent="slate" testId="requests-kpi-total" />
          <KpiTile label="Pending" value={kpis.pending} icon={Clock} accent="amber" testId="requests-kpi-pending" />
          <KpiTile label="In Transit" value={kpis.inTransit} icon={Truck} accent="indigo" testId="requests-kpi-intransit" />
          <KpiTile label="Completed" value={kpis.completed} icon={CheckCircle2} accent="green" testId="requests-kpi-completed" />
          <KpiTile label="Rejected" value={kpis.rejected} icon={XCircle} accent="red" testId="requests-kpi-rejected" />
        </div>
        <DateRangeFilter from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(''); setTo(''); }} testPrefix="requests" />
        <Card className="border-0 shadow-md"><CardContent className="p-0">
          <Table data-testid="requests-table">
            <TableHeader><TableRow>
              <TableHead>Section</TableHead><TableHead>Requested By</TableHead><TableHead>Requested At</TableHead><TableHead>Route</TableHead><TableHead>Type</TableHead>
              <TableHead>Vehicle</TableHead><TableHead>Driver</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                  <TableCell>{r.section}</TableCell>
                  <TableCell className="text-sm" data-testid={`request-requester-${r.id}`}>{r.requested_by_name || '—'}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap" data-testid={`request-time-${r.id}`}>{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell className="text-sm">{route(r)}</TableCell>
                  <TableCell>{r.vehicle_type}</TableCell>
                  <TableCell>{r.vehicle_label || '—'}</TableCell>
                  <TableCell>{r.driver_name || '—'}</TableCell>
                  <TableCell><Badge className={STATUS_COLORS[r.status] || ''} data-testid={`request-status-${r.id}`}>{r.status}</Badge><ClubbedInto r={r} testId={`request-clubbed-${r.id}`} /><ClubbedFrom r={r} testId={`request-clubbed-from-${r.id}`} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {['PENDING', 'ASSIGNED'].includes(r.status) && <>
                      <Button size="sm" variant="outline" onClick={() => openAssign(r)} data-testid={`request-assign-${r.id}`}>{r.status === 'PENDING' ? 'Assign' : 'Reassign'}</Button>
                      <Button size="sm" variant="ghost" className="text-red-500 ml-1" onClick={() => doReject(r)} data-testid={`request-reject-${r.id}`}>Reject</Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-8" data-testid="requests-empty">No requests in this range.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      </main>

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assign Vehicle & Driver</DialogTitle><DialogDescription>Pick a vehicle and driver to fulfil this request.</DialogDescription></DialogHeader>
          {assignFor && <p className="text-sm text-gray-500">{assignFor.section}: {route(assignFor)} ({assignFor.vehicle_type})</p>}
          <div className="space-y-3">
            <div><Label>Vehicle Type</Label>
              <Select value={assignType} onValueChange={(t) => { setAssignType(t); setAssignVeh(''); setAssignDrv(''); }}>
                <SelectTrigger data-testid="assign-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {assignFor && assignType !== assignFor.vehicle_type && <p className="text-[11px] text-amber-600 mt-1" data-testid="assign-type-changed">Requested type was <b>{assignFor.vehicle_type}</b> — assigning a <b>{assignType}</b> instead.</p>}
            </div>
            <div><Label>Vehicle</Label>
              <Select value={assignVeh} onValueChange={(v) => { setAssignVeh(v); setAssignDrv(''); }}>
                <SelectTrigger data-testid="assign-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>{vehicles.filter(v => v.vehicle_type === assignType).map(v => <SelectItem key={v.id} value={v.id}>{v.registration_no} {v.ownership === 'hired' ? '(Hired)' : ''}</SelectItem>)}</SelectContent>
              </Select>
              {assignVeh && vehOnOtherReq(assignVeh) && <p className="text-[11px] text-amber-600 mt-1" data-testid="assign-reassign-warning">⚠ This vehicle is currently assigned to {vehOnOtherReq(assignVeh).section} ({route(vehOnOtherReq(assignVeh))}). Assigning it here will release that request back to Pending.</p>}
            </div>
            <div><Label>Driver (eligible for this vehicle)</Label>
              <Select value={assignDrv} onValueChange={setAssignDrv}>
                <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>{eligibleDrivers().map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note (optional)</Label><Input value={note} onChange={e => setNote(e.target.value)} data-testid="assign-note-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button><Button onClick={doAssign} disabled={assigning} data-testid="assign-confirm-btn">{assigning ? 'Assigning…' : 'Assign'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={hireOpen} onOpenChange={setHireOpen}>
        <DialogContent data-testid="hire-dialog">
          <DialogHeader><DialogTitle>Quick-Add Hired Vehicle</DialogTitle><DialogDescription>Add a hired vehicle so it becomes assignable right away.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Registration No.</Label><Input value={hire.registration_no} onChange={e => setHire(h => ({ ...h, registration_no: e.target.value }))} data-testid="hire-reg-input" /></div>
            <div><Label>Vehicle Type</Label>
              <Select value={hire.vehicle_type} onValueChange={v => setHire(h => ({ ...h, vehicle_type: v }))}>
                <SelectTrigger data-testid="hire-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-gray-500">All active drivers are made eligible for a quick-hired vehicle.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setHireOpen(false)}>Cancel</Button><Button onClick={addHired} data-testid="hire-save-btn">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequestBoard;
